/**
 * The class registry — the single list of every class generated pages may reference.
 *
 * The exporter throws on a class that is not in here. That is what makes brand safety
 * structural: there is no path from the UI to an arbitrary CSS declaration, because a block
 * can only name classes that already exist in this file.
 */

import { assertLonghand } from './longhand.js';
import type { StyleDef } from './styleDef.js';
import { utilityStyles } from './styles/utilities.js';
import { textStyles } from './styles/text.js';
import { mediaStyles } from './styles/media.js';
import { ctaStyles } from './styles/cta.js';
import { typeOverrideStyles } from './styles/typeOverrides.js';

export const ALL_STYLES: StyleDef[] = [
  ...utilityStyles,
  ...textStyles,
  ...mediaStyles,
  ...ctaStyles,
  ...typeOverrideStyles,
];

const BY_NAME = new Map<string, StyleDef>();
for (const def of ALL_STYLES) {
  if (BY_NAME.has(def.name)) {
    throw new Error(
      `Duplicate class name "${def.name}" in the token registry. Names are the dedup key for ` +
        `style output, so two definitions sharing one would silently drop a rule.`,
    );
  }
  BY_NAME.set(def.name, def);
}

// Fail at import time rather than at export time — a shorthand that reaches Webflow is
// invisible (the rule just vanishes on paste), so the earliest possible failure is the
// kindest one.
for (const def of ALL_STYLES) {
  assertLonghand(def.base, `${def.name} (base)`);
  for (const [key, decl] of Object.entries(def.variants ?? {})) {
    if (decl) assertLonghand(decl, `${def.name} (${key})`);
  }
}

// Every combo must point at a base that exists, or Webflow gets a dangling `children` ref.
for (const def of ALL_STYLES) {
  if (def.comb) {
    if (!def.parent) {
      throw new Error(`Combo class "${def.name}" has no parent.`);
    }
    if (!BY_NAME.has(def.parent)) {
      throw new Error(`Combo class "${def.name}" points at unknown parent "${def.parent}".`);
    }
    if (BY_NAME.get(def.parent)?.comb) {
      throw new Error(
        `Combo class "${def.name}" points at "${def.parent}", which is itself a combo. ` +
          `Webflow combos attach to a base class, not to another combo.`,
      );
    }
  }
}

export function getStyle(name: string): StyleDef {
  const def = BY_NAME.get(name);
  if (!def) {
    throw new Error(
      `Unknown class "${name}". Every class a block references must be declared in the token ` +
        `registry — that is what stops generated pages carrying styles Webflow has never seen.`,
    );
  }
  return def;
}

export function hasStyle(name: string): boolean {
  return BY_NAME.has(name);
}

/** Combos that attach to a given base class. */
export function combosOf(baseName: string): StyleDef[] {
  return ALL_STYLES.filter((d) => d.comb && d.parent === baseName);
}

export function allStyleNames(): string[] {
  return [...BY_NAME.keys()];
}
